import React, { useState, useRef, useCallback, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Input, Select, Radio, Button, Spin } from 'antd';
import AccentButton from '../components/AccentButton/AccentButton';
import { UserTag } from '../components/UserTag/UserTag';
import { DefaultAvatar, CogIcon, LockClosedIcon } from '../components/icons';
import SpecModal from '../components/SpecModal/SpecModal';
import SpecBottomSheet from '../components/SpecBottomSheet/SpecBottomSheet';
import SpecAlert from '../components/SpecAlert/SpecAlert';
import EditingBar from '../components/EditingBar/EditingBar';
import UserTypeInfo from '../components/UserTypeInfo/UserTypeInfo';
import type { UserType } from '../components/UserTag/UserTag';
import { radius } from '../theme/tokens/radius';
import { useAuth } from '../contexts/AuthContext';
import { useMyMusician, useMusician, useContributorBands } from '../api/queries';
import { update_musician, upload_musician_photo } from '../api';
import './EditProfile.css';
import './UserProfile.css';

const GENRE_OPTIONS = [
  'Psychedelic', 'Bluegrass', 'Rock', 'Funk', 'Country',
  'Jazz', 'Blues', 'Hip-Hop', 'R&B', 'Pop', 'Metal',
  'Folk', 'Soul', 'Reggae', 'Indie', 'Electronic',
];

const INSTRUMENT_OPTIONS = [
  'Guitar', 'Singer', 'Bass', 'Drums', 'Keyboard', 'Piano',
  'Violin', 'Saxophone', 'Trumpet', 'Banjo', 'Mandolin',
  'Harmonica', 'Pedal Steel', 'Dobro', 'Fiddle',
];

type AccountType = 'musician' | 'fan';

const EditMusicianProfile: React.FC = () => {
  const navigate = useNavigate();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { id } = useParams<{ id?: string }>();

  const { user } = useAuth();
  // With no id, this is the legacy self-only route (/edit-musician-profile) -
  // fetch "my" profile. With an id, it's the real route (/edit-musician-profile/:id)
  // that MusicianDetail.tsx's Edit menu item navigates to, which needs the
  // ownership/admin check below since it's no longer implicitly "me".
  const { data: myMusician, isLoading: myMusicianLoading } = useMyMusician(!id && !!user);
  const { data: idMusician, isLoading: idMusicianLoading } = useMusician(id);
  const musician = id ? idMusician : myMusician;
  const musicianLoading = id ? idMusicianLoading : myMusicianLoading;

  // Mirrors isAdminPermission() on the backend exactly (permissions.includes('*:*')).
  // isOwner is trivially true on the no-id/self path (useMyMusician can only ever
  // return your own record), so this check only actually matters on the id path.
  const isAdmin = !!user?.claims?.permissions?.includes('*:*');
  const isOwner = !!musician && !!user && musician.user_id === user.uid;
  const canEdit = isOwner || isAdmin;

  // Real check, not a static badge: only show the Show Contributor tag if
  // this musician actually contributes to at least one band.
  const { data: contributorBands } = useContributorBands(musician?.user_id, !!user);
  const isShowContributor = (contributorBands?.length ?? 0) > 0;

  const [name, setName] = useState('');
  const [bio, setBio] = useState('');
  const [soundcloudUrl, setSoundcloudUrl] = useState('');
  const [genres, setGenres] = useState<string[]>([]);
  const [instruments, setInstruments] = useState<string[]>([]);
  const [accountType, setAccountType] = useState<AccountType>('musician');
  const [avatarUrl, setAvatarUrl] = useState<string | undefined>(undefined);

  // Seed the form once the real profile loads, without clobbering in-progress
  // edits if the query happens to refetch in the background.
  const initializedRef = useRef(false);
  useEffect(() => {
    if (musician && !initializedRef.current) {
      initializedRef.current = true;
      setName(`${musician.first_name} ${musician.last_name}`.trim());
      setBio(musician.bio || '');
      setSoundcloudUrl(musician.soundcloud_url || '');
      setGenres(musician.genres || []);
      setInstruments(musician.instruments || []);
      setAvatarUrl(musician.photo_url || undefined);
    }
  }, [musician]);

  const [photoSheetOpen, setPhotoSheetOpen] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [switchModalOpen, setSwitchModalOpen] = useState(false);
  const [deletePasscode, setDeletePasscode] = useState('');
  const [openInfoType, setOpenInfoType] = useState<UserType | null>(null);
  const [saving, setSaving] = useState(false);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const [toastMessage, setToastMessage] = useState<string | null>(null);

  const handleSave = useCallback(async () => {
    if (!musician || saving) return;
    setSaving(true);
    const nameParts = name.trim().split(' ');
    try {
      await update_musician(musician.id, {
        first_name: nameParts[0] || '',
        last_name: nameParts.slice(1).join(' '),
        bio,
        soundcloud_url: soundcloudUrl,
        genres,
        instruments,
        // Explicit '' (not omitted) so a removed photo actually clears
        // photo_url server-side instead of leaving the old value untouched -
        // update() only merges the keys it's given.
        photo_url: avatarUrl || '',
        has_photo: !!avatarUrl,
      });
      navigate(id ? `/musicians/${musician.id}` : '/musician-profile', { state: { profileSaved: true } });
    } catch (error: any) {
      setToastMessage(error.message || 'Failed to save profile');
      setSaving(false);
    }
  }, [musician, saving, name, bio, soundcloudUrl, genres, instruments, avatarUrl, id, navigate]);

  const handlePhotoUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    setPhotoSheetOpen(false);
    if (!file || !musician) return;
    setUploadingPhoto(true);
    try {
      const result = await upload_musician_photo(musician.id, file);
      setAvatarUrl(result.url);
    } catch (error: any) {
      setToastMessage(error.message || 'Failed to upload photo');
    } finally {
      setUploadingPhoto(false);
    }
  };

  const handleRemovePhoto = () => {
    setAvatarUrl(undefined);
    setPhotoSheetOpen(false);
  };

  const handleAccountTypeChange = (type: AccountType) => {
    if (type === 'fan' && accountType === 'musician') {
      setSwitchModalOpen(true);
    } else {
      setAccountType(type);
    }
  };

  const confirmAccountSwitch = () => {
    setAccountType('fan');
    setSwitchModalOpen(false);
  };

  if (!user) {
    return (
      <div className="edit-profile-page">
        <div className="user-profile__not-logged-in">
          <div className="user-profile__empty-avatar">
            <DefaultAvatar size={64} />
          </div>
          <h2 className="user-profile__empty-title">Welcome to Spec</h2>
          <p className="user-profile__empty-subtitle">Sign in to edit your musician profile.</p>
        </div>
      </div>
    );
  }

  if (musicianLoading) {
    return (
      <div className="edit-profile-page">
        <div style={{ display: 'flex', justifyContent: 'center', padding: '64px 0' }}>
          <Spin size="large" />
        </div>
      </div>
    );
  }

  if (!musician) {
    return (
      <div className="edit-profile-page">
        <div className="user-profile__not-logged-in">
          <div className="user-profile__empty-avatar">
            <DefaultAvatar size={64} />
          </div>
          <h2 className="user-profile__empty-title">{id ? 'Musician Not Found' : 'No Musician Profile'}</h2>
          <p className="user-profile__empty-subtitle">
            {id ? "This musician doesn't exist or was removed." : "This account doesn't have a linked musician profile yet."}
          </p>
        </div>
      </div>
    );
  }

  if (!canEdit) {
    return (
      <div className="edit-profile-page">
        <div className="user-profile__not-logged-in">
          <div className="user-profile__empty-avatar">
            <DefaultAvatar size={64} />
          </div>
          <h2 className="user-profile__empty-title">Not Authorized</h2>
          <p className="user-profile__empty-subtitle">Only this musician or an admin can edit this profile.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="edit-profile-page">
      <EditingBar
        icon={<CogIcon size="m" tone="white" />}
        title="Editing Profile"
        onCancel={() => navigate(-1)}
        onSave={handleSave}
      />
      <div className="edit-profile-content">
        {/* Avatar Section */}
        <div className="edit-profile-avatar-section edit-profile-avatar-section--row">
          <div
            className="edit-profile-avatar edit-profile-avatar--circle edit-profile-avatar--lg"
            onClick={() => setPhotoSheetOpen(true)}
          >
            {avatarUrl ? (
              <img src={avatarUrl} alt={name} />
            ) : (
              <DefaultAvatar size={48} />
            )}
          </div>
          <div className="edit-profile-avatar-info">
            <div className="edit-profile-avatar-name">{name}</div>
            <div className="edit-profile-avatar-badges">
              <UserTag type="musician" onClick={() => setOpenInfoType('musician')} />
              {isShowContributor && (
                <UserTag type="showContributor" onClick={() => setOpenInfoType('showContributor')} />
              )}
            </div>
          </div>
        </div>
        <Button type="primary" size="small" shape="round" className="edit-profile-update-photo" loading={uploadingPhoto} disabled={uploadingPhoto} onClick={() => setPhotoSheetOpen(true)}>
          Update Photo
        </Button>

        {/* Form */}
        <div className="edit-profile-form">

          {/* Name */}
          <div className="edit-profile-field">
            <label className="edit-profile-label">Name</label>
            <Input
              placeholder="Your name"
              value={name}
              onChange={(e) => setName(e.target.value)}
            />
          </div>

          {/* Bio */}
          <div className="edit-profile-field">
            <label className="edit-profile-label">Bio</label>
            <Input.TextArea
              placeholder="Tell us about yourself..."
              value={bio}
              onChange={(e) => setBio(e.target.value)}
              maxLength={150}
              showCount
              styles={{
                root: { borderRadius: radius.m, marginBottom: 24 },
                textarea: { borderRadius: radius.m, resize: 'none', height: 80 },
                count: { transform: 'translateY(8px)' },
              }}
            />
          </div>

          {/* Song Link */}
          <div className="edit-profile-field">
            <label className="edit-profile-label">Song Link</label>
            <Input
              placeholder="Enter song link"
              value={soundcloudUrl}
              onChange={(e) => setSoundcloudUrl(e.target.value)}
            />
          </div>

          {/* Website/Instagram fields removed - musicianUpdateSchema has no
              matching columns; sending them would 400 the whole save. */}

          {/* Genres */}
          <div className="edit-profile-field">
            <label className="edit-profile-label">Genres</label>
            <div className="edit-profile-tag-selector">
              <Select
                mode="multiple"
                placeholder="Select genres"
                value={genres}
                onChange={setGenres}
                options={GENRE_OPTIONS.map(g => ({ label: g, value: g }))}
                optionFilterProp="label"
              />
            </div>
          </div>

          {/* Instruments */}
          <div className="edit-profile-field">
            <label className="edit-profile-label">Instruments</label>
            <div className="edit-profile-tag-selector">
              <Select
                mode="multiple"
                placeholder="Select instruments"
                value={instruments}
                onChange={setInstruments}
                options={INSTRUMENT_OPTIONS.map(i => ({ label: i, value: i }))}
                optionFilterProp="label"
              />
            </div>
          </div>

          {/* Location */}
          <div className="edit-profile-field">
            <label className="edit-profile-label">Location</label>
            <Input
              prefix="📍"
              placeholder="City, State"
              value="Tulsa, OK."
              disabled
              suffix={<LockClosedIcon size="s" tone="lightGrey" />}
              className="edit-profile-location-input"
            />
            <span className="edit-profile-location-note">
              *our launch is currently limited to Tulsa
            </span>
          </div>

          {/* Account Type */}
          <div className="edit-profile-field">
            <h2 className="edit-profile-label">Account Type</h2>
            <div className="edit-profile-account-types">
              <button
                className={`edit-profile-account-type ${accountType === 'fan' ? 'edit-profile-account-type--selected' : ''}`}
                onClick={() => handleAccountTypeChange('fan')}
              >
                <div className="edit-profile-account-type-info">
                  <span className="edit-profile-account-type-name">Fan Account</span>
                  <div className="edit-profile-account-type-perks">
                    <span className="edit-profile-account-type-perk">• Comment on shows</span>
                    <span className="edit-profile-account-type-perk">• Add photos to shows</span>
                  </div>
                </div>
                <Radio
                  checked={accountType === 'fan'}
                  className="edit-profile-account-type-radio"
                />
              </button>

              <button
                className={`edit-profile-account-type ${accountType === 'musician' ? 'edit-profile-account-type--selected' : ''}`}
                onClick={() => handleAccountTypeChange('musician')}
              >
                <div className="edit-profile-account-type-info">
                  <span className="edit-profile-account-type-name">Musician Account</span>
                  <div className="edit-profile-account-type-perks">
                    <span className="edit-profile-account-type-perk">• Comment on shows</span>
                    <span className="edit-profile-account-type-perk">• Add photos to shows</span>
                    <span className="edit-profile-account-type-perk">• Directory listing and bio</span>
                    <span className="edit-profile-account-type-perk">• Add bands</span>
                    <span className="edit-profile-account-type-perk">• Add shows</span>
                  </div>
                </div>
                <Radio
                  checked={accountType === 'musician'}
                  className="edit-profile-account-type-radio"
                />
              </button>
            </div>
          </div>


          {/* Comments and Photos info */}
          <div className="edit-profile-field">
            <h2 className="edit-profile-label">Comments and Photos</h2>
            <p className="edit-profile-info-block">
            Comments and photos you share on a show can be seen on the show, on the musicians profile, on the bands profile, and on the venue page 🤘
            </p>
          </div>


          {/* Delete Account */}
          <button
            className="edit-profile-danger-link"
            onClick={() => setDeleteModalOpen(true)}
          >
            Delete Account
          </button>
        </div>
      </div>

      {/* Photo Action Sheet */}
      <SpecBottomSheet open={photoSheetOpen} onClose={() => setPhotoSheetOpen(false)}>
        <div className="edit-profile-photo-actions">
          <button
            className="edit-profile-photo-action edit-profile-photo-action--danger"
            onClick={handleRemovePhoto}
          >
            Remove Photo
          </button>
          <button
            className="edit-profile-photo-action edit-profile-photo-action--primary"
            onClick={() => fileInputRef.current?.click()}
          >
            Upload from Photos
          </button>
        </div>
      </SpecBottomSheet>

      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        className="edit-profile-file-input"
        onChange={handlePhotoUpload}
      />

      {/* Account Type Switch Modal */}
      <SpecModal
        open={switchModalOpen}
        onCancel={() => setSwitchModalOpen(false)}
        title="Changing to a Fan account"
        footer={null}
        closable={false}
        centered
        width={340}
      >
        <p className="edit-profile-modal-body">
          Your Musician profile information will be saved, in the case you switch back to a Musician account.
        </p>
        <div className="edit-profile-modal-actions">
          <AccentButton
            size="large"
            shape="round"
            block
            onClick={confirmAccountSwitch}
          >
            Okay
          </AccentButton>
        </div>
      </SpecModal>

      {/* Delete Account Modal */}
      <SpecModal
        open={deleteModalOpen}
        onCancel={() => setDeleteModalOpen(false)}
        title="Delete Account"
        footer={null}
        closable={false}
        centered
        width={340}
      >
        <p className="edit-profile-modal-body">
          Type the "deletemyaccount" to permanently delete account.
        </p>
        <div className="edit-profile-delete-form">
          <span className="edit-profile-delete-label">Delete Passcode:</span>
          <Input
            placeholder="Enter passcode to delete"
            value={deletePasscode}
            onChange={(e) => setDeletePasscode(e.target.value)}
          />
          <Button
            block
            size="large"
            shape="round"
            className="edit-profile-delete-btn"
            disabled={deletePasscode !== 'deletemyaccount'}
            onClick={() => {
              console.log('Account deleted');
              setDeleteModalOpen(false);
            }}
          >
            DELETE ACCOUNT
          </Button>
        </div>
      </SpecModal>

      <UserTypeInfo type="musician" open={openInfoType === 'musician'} onClose={() => setOpenInfoType(null)} />
      <UserTypeInfo type="showContributor" open={openInfoType === 'showContributor'} onClose={() => setOpenInfoType(null)} />

      <SpecAlert message={toastMessage} onDone={() => setToastMessage(null)} />
    </div>
  );
};

export default EditMusicianProfile;
